import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckSquare,
  Calendar,
  CalendarDays,
  User,
  Tag,
  Crosshair,
  FileText,
  Inbox,
  Loader2,
  Clock,
  StickyNote,
  type LucideIcon,
} from 'lucide-react';
import { apiGet } from '../lib/api';

interface TimelineEntry {
  activity_type: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  metadata?: Record<string, string>;
  timestamp: string;
}

const ENTITY_ICONS: Record<string, LucideIcon> = {
  task: CheckSquare,
  meeting_instance: Calendar,
  meeting_series: CalendarDays,
  person: User,
  tag: Tag,
  scope: Crosshair,
  reference_doc: FileText,
  quick_capture: Inbox,
  note: StickyNote,
};

const ENTITY_COLORS: Record<string, string> = {
  task: '#6366f1',
  meeting_instance: '#f59e0b',
  meeting_series: '#f59e0b',
  person: '#3b82f6',
  tag: '#a855f7',
  scope: '#ec4899',
  reference_doc: '#22c55e',
  quick_capture: '#64748b',
  note: '#14b8a6',
};

const ACTIVITY_DESCRIPTIONS: Record<string, string> = {
  task_created: 'Created task',
  task_updated: 'Updated task',
  meeting_created: 'Created meeting',
  note_updated: 'Updated notes for',
  capture_created: 'Quick captured',
  reference_doc_created: 'Created reference doc',
  standalone_note_created: 'Created note',
  standalone_note_updated: 'Updated note',
};

const ACTIVITY_TYPE_OPTIONS = [
  { value: 'task_created', label: 'Tasks created' },
  { value: 'task_updated', label: 'Tasks updated' },
  { value: 'meeting_created', label: 'Meetings created' },
  { value: 'note_updated', label: 'Meeting notes updated' },
  { value: 'standalone_note_created', label: 'Notes created' },
  { value: 'standalone_note_updated', label: 'Notes updated' },
  { value: 'capture_created', label: 'Quick captures' },
  { value: 'reference_doc_created', label: 'Docs created' },
];

function getEntityUrl(entry: TimelineEntry): string {
  switch (entry.entity_type) {
    case 'task': return `/tasks/${entry.entity_id}`;
    case 'meeting_instance': {
      const seriesId = entry.metadata?.series_id;
      return seriesId ? `/meetings/${seriesId}/${entry.entity_id}` : '/meetings';
    }
    case 'meeting_series': return `/meetings/${entry.entity_id}`;
    case 'person': return `/people/${entry.entity_id}`;
    case 'tag': return '/tags';
    case 'scope': return `/projects/${entry.entity_id}`;
    case 'reference_doc': return `/references/${entry.entity_id}`;
    case 'quick_capture': return '/inbox';
    case 'note': return `/notes/${entry.entity_id}`;
    default: return '/';
  }
}

function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getDateGroupLabel(timestamp: string): string {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

const PAGE_SIZE = 50;

export function TimelinePage() {
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const initialLoad = useRef(true);

  async function loadTimeline(offset: number, replace: boolean) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (selectedTypes.size > 0) {
        params.set('activity_type', Array.from(selectedTypes).join(','));
      }
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const data = await apiGet<{ entries: TimelineEntry[]; total: number }>(
        `/timeline?${params}`
      );

      if (replace) {
        setEntries(data.entries);
      } else {
        setEntries(prev => [...prev, ...data.entries]);
      }
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTimeline(0, true);
    initialLoad.current = false;
  }, [selectedTypes, dateFrom, dateTo]);

  function toggleType(type: string) {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  // Group entries by date
  const groupedEntries = new Map<string, TimelineEntry[]>();
  for (const entry of entries) {
    const label = getDateGroupLabel(entry.timestamp);
    if (!groupedEntries.has(label)) groupedEntries.set(label, []);
    groupedEntries.get(label)!.push(entry);
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Timeline</h1>

      {/* Filters */}
      <div className="flex items-start gap-4 mb-6 flex-wrap">
        <div className="flex flex-wrap gap-2">
          {ACTIVITY_TYPE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => toggleType(opt.value)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                selectedTypes.has(opt.value)
                  ? 'bg-accent/10 border-accent text-accent'
                  : 'border-border text-text-muted hover:text-text-secondary hover:border-border-hover'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-2 py-1 bg-bg-input border border-border rounded text-xs text-text-primary"
          />
          <span className="text-text-muted text-xs">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-2 py-1 bg-bg-input border border-border rounded text-xs text-text-primary"
          />
        </div>
        {(selectedTypes.size > 0 || dateFrom || dateTo) && (
          <button
            onClick={() => { setSelectedTypes(new Set()); setDateFrom(''); setDateTo(''); }}
            className="text-xs text-text-muted hover:text-text-primary"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Timeline feed */}
      {loading && entries.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 size={24} className="text-accent animate-spin" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <Clock size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No activity yet</p>
          <p className="text-sm">Create tasks, meetings, or captures to see your timeline.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from(groupedEntries.entries()).map(([dateLabel, groupEntries]) => (
            <div key={dateLabel}>
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 sticky top-0 bg-bg-primary py-1 z-10">
                {dateLabel}
              </h2>
              <div className="space-y-1">
                {groupEntries.map(entry => {
                  const Icon = ENTITY_ICONS[entry.entity_type] ?? FileText;
                  const iconColor = ENTITY_COLORS[entry.entity_type] ?? '#6366f1';
                  const description = ACTIVITY_DESCRIPTIONS[entry.activity_type] ?? entry.activity_type;

                  return (
                    <Link
                      key={`${entry.activity_type}-${entry.entity_id}-${entry.timestamp}`}
                      to={getEntityUrl(entry)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-bg-hover transition-colors group"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: iconColor + '15' }}
                      >
                        <Icon size={16} style={{ color: iconColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-secondary">
                          <span>{description}</span>{' '}
                          <span className="text-text-primary font-medium group-hover:text-accent transition-colors">
                            {entry.entity_name.length > 80
                              ? entry.entity_name.slice(0, 80) + '...'
                              : entry.entity_name}
                          </span>
                        </p>
                      </div>
                      <span className="text-[10px] text-text-muted flex-shrink-0">
                        {formatRelativeTime(entry.timestamp)}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Load more */}
          {entries.length < total && (
            <div className="text-center py-4">
              <button
                onClick={() => loadTimeline(entries.length, false)}
                disabled={loading}
                className="px-4 py-2 text-sm bg-bg-card border border-border rounded-md text-text-secondary hover:bg-bg-hover disabled:opacity-50 transition-colors"
              >
                {loading ? 'Loading...' : `Load more (${total - entries.length} remaining)`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    </div>
  );
}
